import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    ButtonGroup,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';

import './FiltersCard.css';

/** Preset intervals (minutes) for auto-refresh; 0 = off (manual refresh only). */
const AUTO_REFRESH_INTERVAL_MINUTES = [1, 5, 15, 60];

/**
 * Standalone refresh button + optional auto-refresh menu (same UX as FiltersCard header).
 */
function FiltersCardRefreshControl({
    onRefresh,
    refreshLabel,
    showAutoRefreshControl,
    isLoading,
    className,
}) {
    const { t } = useTranslation();

    const onRefreshRef = useRef(onRefresh);
    useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

    const [autoRefreshMinutes, setAutoRefreshMinutes] = useState(0);
    const [autoRefreshMenuOpen, setAutoRefreshMenuOpen] = useState(false);
    const [refreshBarEpoch, setRefreshBarEpoch] = useState(0);

    useEffect(() => {
        if (typeof document === 'undefined' || !onRefresh || !autoRefreshMinutes)
            return;
        const ms = autoRefreshMinutes * 60 * 1000;
        const id = setInterval(() => {
            if (document.visibilityState !== 'visible' || document.hidden) return;
            if (typeof onRefreshRef.current === 'function') onRefreshRef.current();
        }, ms);
        return () => clearInterval(id);
    }, [onRefresh, autoRefreshMinutes, refreshBarEpoch]);

    const resolvedRefreshLabel = refreshLabel ?? t('common.refresh');
    const showAutoRefresh = showAutoRefreshControl !== false && typeof onRefresh === 'function';
    const onRefreshClick = useCallback((e) => {
        if (e) e.stopPropagation();
        if (typeof onRefresh !== 'function') return;
        onRefresh();
        if (autoRefreshMinutes > 0) setRefreshBarEpoch((n) => n + 1);
    }, [onRefresh, autoRefreshMinutes]);
    const toggleAutoRefreshMenu = useCallback((e) => {
        if (e) e.stopPropagation();
        setAutoRefreshMenuOpen((open) => !open);
    }, []);
    const setAutoRefreshInterval = useCallback((minutes) => {
        setAutoRefreshMinutes(minutes);
        setAutoRefreshMenuOpen(false);
    }, []);
    const refreshSplitTitle = t('common.filters_auto_refresh_hint');
    const refreshMainLabel = useMemo(() => {
        if (!autoRefreshMinutes)
            return resolvedRefreshLabel;
        return t(`common.filters_refresh_btn_e_${autoRefreshMinutes}`);
    }, [autoRefreshMinutes, resolvedRefreshLabel, t]);

    if (typeof onRefresh !== 'function')
        return null;

    const bundleClass = [
        'FiltersCard__refresh-bundle',
        'FiltersCard__header-tool',
        autoRefreshMinutes ? 'FiltersCard__refresh-bundle--auto' : '',
    ].filter(Boolean).join(' ');

    const wrapperClass = [
        'FiltersCardRefreshControl',
        'd-inline-flex',
        'align-items-center',
        className,
    ].filter(Boolean).join(' ');

    const refreshBundle = showAutoRefresh ? (
                <div
                    key={`fc-refresh-${autoRefreshMinutes}-${refreshBarEpoch}`}
                    className={bundleClass}
                    title={refreshSplitTitle}
                    role="group"
                    aria-label={refreshSplitTitle}
                    style={autoRefreshMinutes ? { '--fc-refresh-cycle': `${autoRefreshMinutes * 60}s` } : undefined}
                >
                    <ButtonGroup size="sm">
                        <Button
                            type="button"
                            color="primary"
                            outline
                            className="FiltersCard__toolbar-btn FiltersCard__refresh-main text-nowrap"
                            onClick={onRefreshClick}
                            title={refreshSplitTitle}
                        >
                            <i className="fa fa-refresh" aria-hidden="true"></i>&nbsp;{refreshMainLabel}
                        </Button>
                        <Dropdown
                            isOpen={autoRefreshMenuOpen}
                            toggle={toggleAutoRefreshMenu}
                            direction="down"
                        >
                            <DropdownToggle
                                split
                                caret
                                color="primary"
                                outline
                                className="FiltersCard__refresh-split-toggle"
                                aria-label={t('common.filters_auto_menu_aria')}
                                title={t('common.filters_auto_menu_aria')}
                            />
                            <DropdownMenu right>
                                <DropdownItem
                                    active={autoRefreshMinutes === 0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAutoRefreshInterval(0);
                                    }}
                                >
                                    {t('common.filters_auto_item_manual')}
                                </DropdownItem>
                                <DropdownItem divider />
                                {AUTO_REFRESH_INTERVAL_MINUTES.map((m) => (
                                    <DropdownItem
                                        key={m}
                                        active={autoRefreshMinutes === m}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setAutoRefreshInterval(m);
                                        }}
                                    >
                                        {t(`common.filters_auto_item_${m}`)}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>
                    </ButtonGroup>
                </div>
            ) : (
                <Button
                    type="button"
                    size="sm"
                    color="primary"
                    outline
                    className="FiltersCard__toolbar-btn FiltersCard__header-tool FiltersCard__refresh-button text-nowrap"
                    onClick={onRefreshClick}
                    title={resolvedRefreshLabel}
                >
                    <i className="fa fa-refresh" aria-hidden="true"></i>&nbsp;{resolvedRefreshLabel}
                </Button>
            );

    return (
        <span className={wrapperClass}>
            {isLoading && (
                <span className="mr-2 text-muted align-self-center">
                    <i className="fa fa-spinner fa-spin" />
                </span>
            )}
            {refreshBundle}
        </span>
    );
}

FiltersCardRefreshControl.propTypes = {
    onRefresh: PropTypes.func,
    refreshLabel: PropTypes.node,
    showAutoRefreshControl: PropTypes.bool,
    isLoading: PropTypes.bool,
    className: PropTypes.string,
};

FiltersCardRefreshControl.defaultProps = {
    onRefresh: undefined,
    refreshLabel: null,
    showAutoRefreshControl: true,
    isLoading: false,
    className: '',
};

export default FiltersCardRefreshControl;
export { AUTO_REFRESH_INTERVAL_MINUTES };
